import { Component, Input, NgZone, OnInit } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MemberDashboardService } from '../member-dashboard.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

type MemberJournalEntry = {
  id: string;
  notes?: string | null;
  modifiedDateTime?: string | null;
  voiceUrl?: string | null;
  imageUrls?: string[] | null;
};

@Component({
  selector: 'app-member-journal',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  templateUrl: './member-journal.component.html',
  styleUrl: './member-journal.component.css'
})
export class MemberJournalComponent implements OnInit {
  @Input() memberId: any;

  isLoading = false;
  loadError: string | null = null;
  journalEntries: MemberJournalEntry[] = [];

  selectedImage: string | null = null;
  playingAudios: { [key: string]: boolean } = {};
  audioElements: { [key: string]: HTMLAudioElement } = {};
  audioDurations: { [key: string]: string } = {};
  audioCurrentTimes: { [key: string]: number } = {};

  constructor(
    private dashboard: MemberDashboardService,
    private ngZone: NgZone,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.loadJournal();
  }

  loadJournal(): void {
    if (!this.memberId) {
      this.loadError = 'Member is not selected.';
      return;
    }
    this.isLoading = true;
    this.loadError = null;
    this.journalEntries = [];

    this.dashboard.getMemberJournal(this.memberId).subscribe({
      next: (response) => {
        const raw = response?.result !== undefined ? response.result : response;
        const entries = (Array.isArray(raw) ? raw : raw != null ? [raw] : []) as MemberJournalEntry[];
        // Sort newest first by modifiedDateTime
        entries.sort((a, b) => {
          const aTime = a?.modifiedDateTime ? new Date(a.modifiedDateTime).getTime() : 0;
          const bTime = b?.modifiedDateTime ? new Date(b.modifiedDateTime).getTime() : 0;
          return bTime - aTime;
        });
        this.journalEntries = entries;

        // Preload audio metadata for duration + time updates
        this.journalEntries.forEach((entry) => {
          if (entry?.voiceUrl && entry?.id) {
            this.preloadAudio(entry.voiceUrl, entry.id);
          }
        });
        this.isLoading = false;
      },
      error: () => {
        this.loadError = 'Unable to load journal data. Please try again.';
        this.isLoading = false;
      }
    });
  }

  formatLoggedAt(dateTimeString?: string | null): string {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    return formatDate(date, 'MMMM d, y h:mm a', 'en');
  }

  preloadAudio(audioUrl: string, id: string): void {
    const audio = new Audio(audioUrl);
    audio.preload = 'metadata';
    audio.loop = false;
    this.audioCurrentTimes[id] = 0;

    audio.addEventListener('loadedmetadata', () => {
      const minutes = Math.floor(audio.duration / 60);
      const seconds = Math.floor(audio.duration % 60);
      this.audioDurations[id] = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    });

    audio.addEventListener('error', () => {
      this.audioDurations[id] = '0:00';
      this.audioCurrentTimes[id] = 0;
    });

    audio.addEventListener('timeupdate', () => {
      this.ngZone.run(() => {
        this.audioCurrentTimes[id] = audio.currentTime;
        if (audio.duration && audio.currentTime >= audio.duration) {
          this.playingAudios[id] = false;
          this.audioCurrentTimes[id] = 0;
        }
      });
    });

    audio.addEventListener('play', () => {
      this.ngZone.run(() => {
        this.playingAudios[id] = true;
      });
    });

    audio.addEventListener('pause', () => {
      this.ngZone.run(() => {
        this.playingAudios[id] = false;
      });
    });

    audio.addEventListener('ended', () => {
      this.ngZone.run(() => {
        this.playingAudios[id] = false;
        this.audioCurrentTimes[id] = 0;
        audio.currentTime = 0;
      });
    });

    this.audioElements[id] = audio;
  }

  toggleAudio(audioUrl: string, id: string): void {
    if (!this.audioElements[id]) {
      this.preloadAudio(audioUrl, id);
    }

    const audio = this.audioElements[id];
    if (this.audioCurrentTimes[id] === undefined) {
      this.audioCurrentTimes[id] = 0;
    }

    const isCurrentlyPlaying = !audio.paused && !audio.ended;

    if (isCurrentlyPlaying) {
      audio.pause();
      this.playingAudios[id] = false;
      return;
    }

    // Pause all other audios
    Object.keys(this.audioElements).forEach((key) => {
      if (key !== id && this.audioElements[key] && !this.audioElements[key].paused) {
        this.audioElements[key].pause();
        this.playingAudios[key] = false;
      }
    });

    if (audio.ended) {
      audio.currentTime = 0;
      this.audioCurrentTimes[id] = 0;
    } else {
      this.audioCurrentTimes[id] = audio.currentTime || 0;
    }

    audio.play().then(() => {
      this.audioCurrentTimes[id] = audio.currentTime || 0;
    }).catch((error) => {
      console.error('Error playing audio:', error);
      this.playingAudios[id] = false;
    });
  }

  getAudioDuration(_: string, id: string): string {
    let currentTime = 0;
    if (this.audioElements[id]) {
      currentTime = this.audioElements[id].currentTime || 0;
      this.audioCurrentTimes[id] = currentTime;
    } else {
      currentTime = this.audioCurrentTimes[id] || 0;
    }

    let totalSeconds = 0;
    if (this.audioElements[id] && this.audioElements[id].duration && !isNaN(this.audioElements[id].duration)) {
      totalSeconds = this.audioElements[id].duration;
    } else if (this.audioDurations[id] && this.audioDurations[id] !== '0:00') {
      const parts = this.audioDurations[id].split(':');
      if (parts.length === 2) {
        totalSeconds = parseInt(parts[0]) * 60 + parseInt(parts[1]);
      }
    }

    const currentSeconds = Math.ceil(currentTime);
    const currentMinutes = Math.floor(currentSeconds / 60);
    const currentSecondsRemainder = currentSeconds % 60;
    const currentTimeFormatted = `${currentMinutes.toString().padStart(2, '0')}:${currentSecondsRemainder.toString().padStart(2, '0')}`;

    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalSecondsRemainder = Math.floor(totalSeconds % 60);
    const totalTimeFormatted = `${totalMinutes.toString().padStart(2, '0')}:${totalSecondsRemainder.toString().padStart(2, '0')}`;

    return `${currentTimeFormatted}/${totalTimeFormatted}`;
  }

  isAudioPlaying(id: string): boolean {
    if (this.audioElements[id]) {
      const audio = this.audioElements[id];
      const isPlaying = !audio.paused && !audio.ended;
      if (isPlaying !== this.playingAudios[id]) {
        this.playingAudios[id] = isPlaying;
      }
      return isPlaying;
    }
    return false;
  }

  openImageLightbox(imageUrl: string): void {
    this.selectedImage = imageUrl;
  }

  closeImageLightbox(): void {
    this.selectedImage = null;
  }

  /**
   * Show journal notes like the member app: preserve line breaks and decode
   * literal JSON-style escapes when the API returns them as text (e.g. \uD83D\uDC4B, \\n).
   */
  formatJournalNotesDisplay(notes: string | null | undefined): string {
    if (notes == null || notes === '') {
      return '';
    }
    let s = notes;
    // Literal escape sequences stored inside the string
    s = s.replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n').replace(/\\r/g, '\n').replace(/\\t/g, '\t');
    // Astral plane: surrogate pair \uD83D\uDC4B → single code point
    s = s.replace(/\\u([dD][89a-fA-F][0-9a-f]{2})\\u([dD][c-fC-F][0-9a-f]{2})/g, (_, hi, lo) => {
      const h = parseInt(hi, 16);
      const l = parseInt(lo, 16);
      return String.fromCodePoint((h - 0xd800) * 0x400 + (l - 0xdc00) + 0x10000);
    });
    s = s.replace(/\\u([0-9a-fA-F]{4})/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
    return s;
  }

  /** Rich journal body: **bold**, _italic_, &lt;u&gt;…&lt;/u&gt; underline (member app). */
  getJournalNotesHtml(notes: string | null | undefined): SafeHtml {
    if (notes == null || notes === '') {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }
    return this.sanitizer.bypassSecurityTrustHtml(this.buildJournalNotesHtml(notes));
  }

  private buildJournalNotesHtml(raw: string): string {
    const normalized = this.formatJournalNotesDisplay(raw);
    const re = /<\s*u\b[^>]*>([\s\S]*?)<\s*\/\s*u\s*>/gi;
    const parts: { u: boolean; body: string }[] = [];
    let lastIdx = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(normalized)) !== null) {
      if (m.index > lastIdx) {
        parts.push({ u: false, body: normalized.slice(lastIdx, m.index) });
      }
      parts.push({ u: true, body: m[1] });
      lastIdx = re.lastIndex;
    }
    if (lastIdx < normalized.length) {
      parts.push({ u: false, body: normalized.slice(lastIdx) });
    }
    if (parts.length === 0) {
      parts.push({ u: false, body: normalized });
    }
    let out = '';
    for (const p of parts) {
      const inner = this.journalInlineToHtml(p.body);
      out += p.u ? `<u class="journal-note-u">${inner}</u>` : inner;
    }
    return out;
  }

  private escapeHtml(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  private journalInlineToHtml(fragment: string): string {
    let h = this.escapeHtml(fragment);
    h = h.replace(/\*\*([\s\S]*?)\*\*/g, '<strong>$1</strong>');
    h = h.replace(/_([\s\S]*?)_/g, '<em>$1</em>');
    h = h.replace(/\r\n|\n|\r/g, '<br/>');
    return h;
  }
}
